import * as Chess from 'chess.js'

export const isString = (s) => {
  return typeof s === 'string';
};

export const deepCopy = thing => {
  return JSON.parse(JSON.stringify(thing));
};

export const START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR';
export const COLUMNS = 'abcdefgh'.split('');

export const whitePieces = ['wK', 'wQ', 'wR', 'wB', 'wN', 'wP'];
export const blackPieces = ['bK', 'bQ', 'bR', 'bB', 'bN', 'bP'];

export const getSquareColor = (square) =>
  square.charCodeAt(0) % 2 ^ square.charCodeAt(1) % 2 ? 'white' : 'black';

export const validSquare = (square) => {
  return isString(square) && square.search(/^[a-h][1-8]$/) !== -1;
};

export const validMove = (move) => {
  // move should be a string
  if (!isString(move)) return false;

  // move should be in the form of "e2-e4", "f6-d5"
  const squares = move.split('-');
  if (squares.length !== 2) return false;

  return validSquare(squares[0]) && validSquare(squares[1]);
};

export const validPieceCode = (code) => {
  return isString(code) && code.search(/^[bw][KQRNBP]$/) !== -1;
};

const squeezeFenEmptySquares = (fen) => {
  return fen
    .replace(/11111111/g, '8')
    .replace(/1111111/g, '7')
    .replace(/111111/g, '6')
    .replace(/11111/g, '5')
    .replace(/1111/g, '4')
    .replace(/111/g, '3')
    .replace(/11/g, '2');
};

const expandFenEmptySquares = (fen) => {
  return fen
    .replace(/8/g, '11111111')
    .replace(/7/g, '1111111')
    .replace(/6/g, '111111')
    .replace(/5/g, '11111')
    .replace(/4/g, '1111')
    .replace(/3/g, '111')
    .replace(/2/g, '11');
};

export const validFen = (fen) => {
  if (!isString(fen)) return false;

  // cut off any move, castling, etc info from the end
  // we're only interested in position information
  fen = fen.replace(/ .+$/, '');

  // expand the empty square numbers to just 1s
  fen = expandFenEmptySquares(fen);

  // FEN should be 8 sections separated by slashes
  const chunks = (fen).split('/');
  if (chunks.length !== 8) return false;

  // check each section
  for (let i = 0; i < 8; i++) {
    if (chunks[i].length !== 8 || chunks[i].search(/[^kqrnbpKQRNBP1]/) !== -1) {
      return false;
    }
  }

  return true;
};

export const validPositionObject = (pos) => {
  if (typeof pos !== 'object' || pos === null) {
    return false;
  }

  for (const [square, piece] of Object.entries(pos)) {
    if (!validSquare(square) || !validPieceCode(piece)) {
      return false;
    }
  }

  return true;
};

// convert FEN piece code to bP, wK, etc
const fenToPieceCode = (piece) => {
  // black piece
  if (piece.toLowerCase() === piece) {
    return 'b' + piece.toUpperCase();
  }

  // white piece
  return 'w' + piece.toUpperCase();
};

// convert bP, wK, etc code to FEN structure
const pieceCodeToFen = (piece) => {
  const pieceCodeLetters = piece.split('');

  // white piece
  if (pieceCodeLetters[0] === 'w') {
    return pieceCodeLetters[1].toUpperCase();
  }

  // black piece
  return pieceCodeLetters[1].toLowerCase();
};

// convert FEN string to position object
// returns false if the FEN string is invalid
export const fenToObj = (fen) => {
  if (!validFen(fen)) return false;

  // cut off any move, castling, etc info from the end
  // we're only interested in position information
  fen = fen.replace(/ .+$/, '');

  const rows = fen.split('/');
  const position: PositionObject = {};

  let currentRow = 8;
  for (let i = 0; i < 8; i++) {
    const row = rows[i].split('');
    let colIdx = 0;

    // loop through each character in the FEN section
    for (let j = 0; j < row.length; j++) {
      // number / empty squares
      if (row[j].search(/[1-8]/) !== -1) {
        const numEmptySquares = parseInt(row[j], 10);
        colIdx = colIdx + numEmptySquares;
      } else {
        // piece
        const square = COLUMNS[colIdx] + currentRow;
        position[square] = fenToPieceCode(row[j]);
        colIdx = colIdx + 1;
      }
    }

    currentRow = currentRow - 1;
  }

  return position;
};

export const START_POSITION = fenToObj(START_FEN);

// position object to FEN string
// returns false if the obj is not a valid position object
export const objToFen = (obj) => {
  if (!validPositionObject(obj)) return false;

  let fen = '';

  let currentRow = 8;
  for (let i = 0; i < 8; i++) {
    for (let j = 0; j < 8; j++) {
      const square = COLUMNS[j] + currentRow;

      // piece exists
      if (obj.hasOwnProperty(square)) {
        fen = fen + pieceCodeToFen(obj[square]);
      } else {
        // empty space
        fen = fen + '1';
      }
    }

    if (i !== 7) {
      fen = fen + '/';
    }

    currentRow = currentRow - 1;
  }

  // squeeze the empty numbers together
  fen = squeezeFenEmptySquares(fen);

  return fen;
};

export const normalizePozition = (position) => {
  if (position == null) {
    return {};
  }

  // start position
  if (isString(position) && position.toLowerCase() === 'start') {
    position = deepCopy(START_POSITION);
  }

  // convert FEN to position object
  if (validFen(position)) {
    position = fenToObj(position);
  }
  return position;
};

export const enforceCastlingRights = (fen) => {
  const chess = new Chess(fen);
  const board = chess.board();

  // Find kings and rooks
  const whiteKing = board[7][4];
  const blackKing = board[0][4];
  const whiteRookA = board[7][0];
  const whiteRookH = board[7][7];
  const blackRookA = board[0][0];
  const blackRookH = board[0][7];

  let rights = '';

  // White castling rights
  if (whiteKing && whiteKing.type === 'k' && whiteKing.color === 'w') {
    if (whiteRookH && whiteRookH.type === 'r' && whiteRookH.color === 'w') rights += 'K';
    if (whiteRookA && whiteRookA.type === 'r' && whiteRookA.color === 'w') rights += 'Q';
  }

  // Black castling rights
  if (blackKing && blackKing.type === 'k' && blackKing.color === 'b') {
    if (blackRookH && blackRookH.type === 'r' && blackRookH.color === 'b') rights += 'k';
    if (blackRookA && blackRookA.type === 'r' && blackRookA.color === 'b') rights += 'q';
  }

  // Replace 6th FEN field (castling rights)
  const parts = fen.split(' ');
  parts[2] = rights || '-';
  return parts.join(' ');
}

